import { ApiError, apiErrorCode } from "@/lib/api";
import {
  readValidationReason,
  uncodedRefusalMessage,
  VALIDATION_SUMMARY,
  validationFailures,
  validationReasonCopy,
} from "@/lib/errors";
import { scopedRosterName } from "@/lib/bondRoster";
import { SSN_DUPLICATE, duplicateSsnMessage } from "@/lib/bondRosterChecks";
import {
  EIN_ALREADY_EXISTS,
  EIN_CONFLICT_COPY,
  einConflictPlacement,
} from "@/lib/einConflict";

// BRK-S092 — putting a rejected save's reasons under the inputs they belong to.
//
// A bond step save that fails validation answers with structured detail:
//   { error: "<display-safe summary>",
//     details: { code, fields: [{ path, reason }, …] } }
// Nothing consumed `fields`, so the broker got the raw message — which during
// live testing was the vendor's endpoint URL. This module turns that envelope
// into (a) a reason per rendered field and (b) one step-level line.
//
// TWO PATH GRAMMARS ARRIVE, and they are not in conflict — each names its
// subject the way that subject was addressed (settled with the surety agent):
//
//   INVALID_FIELD_VALUE (422) validates the path-keyed values we SENT, so it
//     echoes our own keys: `personal[].ssn`. Byte-identical to a bag key —
//     match it directly, and never strip it.
//   VALIDATION_FAILED (400) parses a request body that is a real array, and
//     LENDFLOW_REJECTED (422) names the element the vendor actually rejected,
//     so both carry a CONCRETE index: `personal[0].ssn`. Strip the index to
//     find the field the config named.
//
// A non-zero index is deliberately NOT anchored: the flow renders one person
// (BRK-S094 adds rows), so `personal[1].ssn` has no control to sit under, and
// putting owner #2's error on owner #1's input is worse than not placing it.
//
// One code names NO fields at all — `NO_OWNER_ON_ACCOUNT` (400, PMS-S244) — and
// is handled on its own below, ahead of the grammars above.

/**
 * The `details.code` vocabulary, as PMS's spec ENUMERATES it (PMS-S238, adopted
 * in BRK-S110). It was an open map until then, read here by hand.
 *
 * Pinned by the drift guard against the published enum, so a rename upstream
 * fails CI instead of quietly turning a specific, actionable sentence into a
 * generic one on a broker's screen — the failure mode that is invisible in
 * every test that does not name the code.
 *
 * THE READER IS STILL DEFENSIVE, and deliberately so. The contract's own
 * wording: "TREAT AN UNRECOGNISED VALUE AS A GENERIC FAILURE, never as fatal:
 * discovery relays its own codes and adds them on its own cadence, so this list
 * grows without a PMS release." So this union types what BRK RECOGNISES; it is
 * never used to narrow what BRK will ACCEPT. `codeOf` returns a bare `string`
 * for exactly that reason, and every branch below is an equality test against a
 * known code rather than an exhaustive switch. A closed reader that broke on a
 * code it had not adopted would turn an upstream addition into an outage.
 */
export type ServerErrorCode =
  /**
   * BRK-S170 — the server would not take one of the eligibility attestations,
   * and `details.index` says which. PUBLISHED as of the wave-1 re-pin; it is
   * read in `bondEligibility.ts` rather than here, because it anchors onto a
   * checkbox on the code-owned first step and not onto a configured field in a
   * value bag — the two grammars this module places reasons by.
   */
  | "ELIGIBILITY_NOT_CONFIRMED"
  /**
   * BRK-S171 — two refusals about the ROSTER AS A WHOLE rather than about one
   * field: the owners' shares exceed 100%, and two people share an email.
   * PUBLISHED as of the wave-1 re-pin, and read in `bondRosterChecks.ts` rather
   * than here, because neither names a field — they name PEOPLE, and they
   * anchor to owner CARDS through the roster's own `cardErrors` channel.
   */
  | "OWNERSHIP_EXCEEDS_TOTAL"
  | "DUPLICATE_ROSTER_EMAIL"
  /**
   * BRK-S317 — two roster people share an SSN (SUR-S220's backstop; adopted
   * ahead of the pin, published by the sprint-10 release re-pin). Unlike the two above it DOES name fields — both
   * people's SSN, by path + ownerId — so it anchors here, on those fields, in
   * portal copy (the server's reason is never shown).
   */
  | "SSN_DUPLICATE"
  /**
   * BRK-S172's refusal, adopted at the wave-1 re-pin: the business declared a
   * parent entity and the parent's own block was not filled in. Recognised so
   * the code is not a generic sentence; it names no field of its own, so it
   * lands as the step-level line the same way `NO_OWNER_ON_ACCOUNT` does.
   */
  | "PARENT_ENTITY_NOT_DECLARED"
  | "NO_OWNER_ON_ACCOUNT"
  | "INVALID_PHONE"
  | "VALIDATION_FAILED"
  | "INVALID_FIELD_VALUE"
  | "FINAL_STEP_INVALID"
  | "NO_CONFIGURATION"
  | "UNKNOWN_STEP"
  | "WRONG_STEP_TYPE"
  | "ALREADY_SUBMITTED"
  /**
   * BRK-S181 (PMS-S479, pinned): a write on a CANCELLED order, and a cancel
   * refused because the pre-approval is already decided. Read by
   * `isOrderCancelled` / `isOrderDecided` in the seam. Neither is a double
   * submit: `isAlreadySubmitted` matches ALREADY_SUBMITTED alone (BRK-S113).
   */
  | "ORDER_CANCELLED"
  | "ORDER_HAS_DECISION"
  /**
   * BRK-S182 (PMS-S483 / PMS-S484, pinned): the GIA routes' refusals. The four
   * signer codes carry `details.fields: [{ownerId, reason}]` and land on the
   * signer rows; the rest are order-level and land beside the Send button.
   * Read in `brokerGia.ts`.
   */
  | "GIA_SIGNER_EMAIL_DUPLICATE"
  | "GIA_SIGNER_EMAIL_INVALID"
  | "GIA_SIGNER_EMAIL_MISSING"
  | "GIA_SIGNER_UNKNOWN"
  | "GIA_SIGNER_SLOTS_EXCEEDED"
  /**
   * BRK-S189 (PMS-S512, pinned): a married owner whose GIA template has no
   * spouse seat. `details.slot` names the missing role (e.g. "Indemnitor 2
   * Spouse"); the page never prints it or the code.
   */
  | "GIA_SPOUSE_SLOT_MISSING"
  | "GIA_VALUES_MISSING"
  | "GIA_NOT_REQUIRED"
  /**
   * BRK-S280 (PMS-S608, pinned): the account's GIA requirement does not apply
   * from this order — since D15 (PMS-S839), nothing on the account is filed
   * yet or the order is no longer open. `GIA_ORDER_NOT_APPROVED`, which it
   * replaced, left the contract at the BRK-S511 re-pin. Recognised only; it
   * lands as PMS's own sentence.
   */
  | "GIA_NOT_APPLICABLE"
  /** PMS-S839 (pinned at BRK-S511): a remind or resend with no GIA out. */
  | "GIA_NOT_OUT"
  | "GIA_INDEMNITY_INCOMPLETE"
  | "GIA_TEMPLATE_UNAVAILABLE"
  | "GIA_ALREADY_OPEN"
  | "UNKNOWN_OWNER"
  | "AMBIGUOUS_PRIMARY_OWNER"
  | "DUPLICATE_OWNER"
  | "TEMPLATE_SHAPE_MISMATCH"
  | "LENDFLOW_REJECTED"
  | "UPSTREAM_ERROR"
  /**
   * Published by the sprint-10 release re-pin (PMS release/sprint-10). Each is
   * read where its route is: the withdraw / clone refusals in `bondLifecycle.ts`
   * (PMS-S718), the re-apply refusals in `preapprovalApplication.ts`
   * (PMS-S719), and a pre-approval with no declared pulls (PMS-S721) degrades
   * to the generic sentence like any step-level refusal.
   */
  | "WITHDRAW_NOT_ALLOWED"
  | "CLONE_NOT_ALLOWED"
  | "APPLICATION_OPEN"
  | "REAPPLY_NOT_ALLOWED"
  | "PREAPPROVAL_PULLS_UNDECLARED"
  /**
   * Published by the re-pin onto epic/PMS-E042 (BRK-S511). Each is read where
   * its route is: the edit and rider refusals (`EDIT_NOT_ALLOWED`,
   * `FIELD_LOCKED`, `RIDER_NOT_ALLOWED`, `RIDER_PENDING`, `RIDER_NOT_PENDING`,
   * `NO_ISSUED_BOND`) in `bondEdit.ts` / `bondServicing.ts`, the claim's
   * `CLAIM_NOT_ALLOWED` (PMS-S851) and `NO_ISSUED_BOND` in `bondServicing.ts`,
   * the bond-form refusals (PMS-S848) in `bondForms.ts`, a message refused on
   * a discarded draft (PMS-S842), a document refused by the platform's file
   * rules, and the account write's duplicate EIN. An unrecognised one still
   * degrades to PMS's own sentence.
   */
  | "EDIT_NOT_ALLOWED"
  | "FIELD_LOCKED"
  | "RIDER_NOT_ALLOWED"
  | "RIDER_PENDING"
  | "RIDER_NOT_PENDING"
  | "NO_ISSUED_BOND"
  | "CLAIM_NOT_ALLOWED"
  | "BOND_FORMS_LOCKED"
  | "BOND_FORM_NOT_FOUND"
  | "MESSAGE_REJECTED"
  | "DOCUMENT_REJECTED"
  | "EIN_ALREADY_EXISTS";

/** One field the server rejected, addressed the way it addressed it. */
interface ServerFieldError {
  path: string;
  reason: string;
  /** WHOSE field, on a roster step (PMS-S393). Absent on every other refusal,
   *  and on a roster one the server chose not to attribute. */
  ownerId?: string;
}

export interface AnchoredServerError {
  /** Reason per field bag key — merges into the same `invalid` state the
   *  client-side rules (BRK-S091) use, so there is ONE inline error channel. */
  byField: Record<string, string>;
  /** The step-level line. Never empty (NN #7). */
  summary: string;
}

/** The floor when there is nothing useful left to say. Mirrors discovery's own
 *  fallback, so the broker sees the same sentence whichever side produced it. */
const GENERIC = "The vendor rejected one or more fields.";

// ── NO_OWNER_ON_ACCOUNT (400) — the insured has nobody with a share ──────────
//
// PMS-S244 narrowed a refusal here. The old one, `ELEMENT_ID_UNKNOWN`, said the
// server could not work out WHICH owner a repeated value belonged to — a
// question the owner roster now answers outright, so the refusal is RETIRED and
// nothing in this repo handles it any more.
//
// What replaced it is smaller and factual: the insured has no owner holding an
// ownership share at all, so there is no primary owner for a repeated block to
// resolve to. That is not a mystery to report — it is a gap the broker can
// close, which is why it gets its own sentence instead of the vendor's text.
//
// NOW DESCRIBED BY THE PINNED CONTRACT (BRK-S110). PMS-S238 enumerates
// `details.code`, so this code — like `INVALID_FIELD_VALUE` and
// `VALIDATION_FAILED` — is pinned by the drift guard rather than read out of a
// bag the spec declined to describe. The reading stays defensive all the same:
// an unrecognised code falls through to the generic handling rather than to a
// branch that assumes this one.
const NO_OWNER_ON_ACCOUNT: ServerErrorCode = "NO_OWNER_ON_ACCOUNT";

/** The rider's and the claim's "that bond is not issued" (BRK-S511). */
const NO_ISSUED_BOND: ServerErrorCode = "NO_ISSUED_BOND";

/** The one code whose `fields` echo the bag keys BRK sent, rather than the
 *  vendor's own indexed spelling — see the grammars note at the top. */
const INVALID_FIELD_VALUE: ServerErrorCode = "INVALID_FIELD_VALUE";

/** The closed-set validation refusal (PMS-S609) — read in the portal's words. */
const VALIDATION_FAILED: ServerErrorCode = "VALIDATION_FAILED";

/** The submit's refusal of the final step (BRK-S289, BRK-S296). Every reason it
 *  carries is already portal copy by the time it is anchored, and its `error`
 *  names raw keys — so it is never the summary. */
const FINAL_STEP_INVALID: ServerErrorCode = "FINAL_STEP_INVALID";

/** Configured label per field key — how a reason names a field to the broker. */
export type FieldLabels = Readonly<Record<string, string>>;

// ── FINAL_STEP_INVALID `details.invalid` (BRK-S296) ─────────────────────────
//
// Surety's `validateFinalStepData` (surety service, relayed by PMS)
// rejects a present final-step value with `{path: <key>, reason}`, where the
// reason is free text written for a developer: "expected a number", "expected a
// date as YYYY-MM-DD", "expected one of: days, weeks, months", "must not be
// earlier than startDate". It names raw keys and formats, so it is READ into
// the portal's words here and never shown (BRK-S279). An unrecognised reason is
// the generic "This isn't valid." — surety adds reasons on its own cadence.

/** "must not be earlier than <key>" — the ordered-dates rule (SUR-S151). */
const EARLIER_THAN = /^must not be earlier than (\S+)$/;

function finalStepReasonCopy(reason: unknown, labels: FieldLabels): string {
  if (typeof reason !== "string") return validationReasonCopy("invalid");
  const earlier = EARLIER_THAN.exec(reason.trim());
  if (earlier !== null) {
    const label = labels[earlier[1]];
    return label === undefined
      ? "This date is earlier than it can be."
      : `This can’t be earlier than the ${label}.`;
  }
  if (reason === "expected a number") return "Enter a number.";
  if (reason === "expected a percentage between 0 and 100") {
    return "Enter a percentage between 0 and 100.";
  }
  if (reason.startsWith("expected a date")) return "Enter a valid date.";
  if (reason.startsWith("expected one of")) return "Choose one of the options.";
  if (reason === "expected a phone number")
    return "Enter a valid phone number.";
  return validationReasonCopy("invalid");
}

/** What the broker should actually do about it. */
const NO_OWNER_SUMMARY =
  "This insured has no owner with an ownership share on file. Add an owner and give them a share, then try again.";

/**
 * The bag key that captures an ownership share, among the keys the step drew.
 *
 * Matched on the CONFIG PATH's last segment — the same path grammar every other
 * rule in this file works in, and the spelling BRK is handed by the resolved
 * configuration rather than one guessed from a vendor payload. A configuration
 * that names the share something else simply yields no anchor, and the sentence
 * lands at step level: reported to the broker either way, never mis-anchored
 * onto a field that is not the one to fix.
 */
const OWNERSHIP_SHARE_SEGMENT = "ownershipPercentage";

function ownershipShareField(rendered: ReadonlySet<string>): string | null {
  for (const name of rendered) {
    if (name.slice(name.lastIndexOf(".") + 1) === OWNERSHIP_SHARE_SEGMENT) {
      return name;
    }
  }
  return null;
}

/** The floor for a failure that isn't a structured rejection at all. */
const UNKNOWN = "Something went wrong. Please try again.";

/**
 * Vendor text with anything routable removed.
 *
 * Discovery sanitises this itself (SUR-S047) and asserts no response body
 * carries a vendor path — so this is belt-and-braces, NOT the primary defence.
 * It stays because the text originates two services upstream and one endpoint
 * path reaching a broker is a worse trade than a rule that occasionally eats a
 * slash. Mirrors discovery's rules deliberately: the SDK label by shape, then
 * absolute URLs, then tokens that START a word with a slash (so `and/or` and
 * `01/02/2026` survive).
 */
function stripRoutable(text: string): string {
  return text
    .replace(/^[A-Z]+\s+\S+\s+returned\s+\d{3}\s*:?\s*/, "")
    .replace(/\S*:\/\/\S*/g, " ")
    .replace(/(^|\s)\/[^\s,;]*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * The fields a refusal names, keeping only well-formed entries — a malformed
 * payload must degrade to "no anchoring", never throw (NN #7).
 *
 * TWO ENVELOPE SHAPES, and the second is why this function exists rather than
 * an inline read (BRK-S141):
 *
 *   • `details.fields` — an ARRAY of `{path, reason}`. Every structured
 *     refusal in the bond flow uses it.
 *   • `details.field` — a bare STRING naming one field, with the sentence in
 *     the top-level `error`. The bid result answers this way
 *     (PMS-S317/SUR-S152), and upstream's own note is that its first draft sent
 *     the array and "BRK would have read `undefined` for the field to attach
 *     the error to". Reading only the plural is not a smaller bug than reading
 *     the wrong key: the broker gets the sentence at panel level and the input
 *     they must fix is never marked.
 *
 * A third, `details.missingRequired` (a bare list of keys on the submit's
 * FINAL_STEP_INVALID), is read alongside them since BRK-S289 — see below.
 *
 * The bid result's three codes — `BID_EXPLANATION_REQUIRED`,
 * `BID_AMOUNT_REQUIRED`, `BID_AMOUNT_NOT_APPLICABLE` — are deliberately NOT
 * added to `ServerErrorCode` above. Nothing branches on them (the envelope
 * already names the field and carries the sentence), and the union is pinned
 * against the enum the contract publishes, which does not include them yet: a
 * member added ahead of that would fail the vocabulary guard for a code no
 * reader consults. This is the module's own rule at work — the union types what
 * BRK RECOGNISES, never what it will ACCEPT.
 *
 * The singular form has no `reason` of its own, so it borrows the envelope's
 * `error` — which is the sentence upstream wrote for exactly that field.
 */
function fieldErrorsOf(
  body: unknown,
  labels: FieldLabels = {},
): ServerFieldError[] {
  if (!isRecord(body)) return [];
  const details = body.details;
  if (!isRecord(details)) return [];

  const out: ServerFieldError[] = [];
  const fields = details.fields;
  if (Array.isArray(fields)) {
    for (const entry of fields) {
      if (!isRecord(entry)) continue;
      const { path, reason, ownerId } = entry;
      if (typeof path !== "string" || typeof reason !== "string") continue;
      if (path === "" || reason.trim() === "") continue;
      out.push({
        path,
        reason,
        ...(typeof ownerId === "string" && ownerId !== "" ? { ownerId } : {}),
      });
    }
  }
  // A third shape (BRK-S289): the submit's FINAL_STEP_INVALID (400) names
  // unanswered final-step fields in `details.missingRequired`, a bare list of
  // field KEYS with no reason of its own — PMS relays discovery's envelope
  // verbatim. Each key gets the portal's "required" sentence; the server's
  // `error` ("Required: bondAmount, …") names raw keys and is never shown.
  // This is how surety's at-least-one-of bond-penalty refusal (SUR-S211),
  // which names BOTH keys, reaches the composite control.
  const missingRequired = details.missingRequired;
  if (Array.isArray(missingRequired)) {
    for (const key of missingRequired) {
      if (typeof key !== "string" || key === "") continue;
      out.push({ path: key, reason: validationReasonCopy("required") });
    }
  }
  // …and its `details.invalid` (BRK-S296): `{path, reason}` per final-step
  // value that fails its control type or a cross-field rule. The path is a bare
  // key, as in `missingRequired`; the reason becomes portal copy right here, so
  // surety's text never travels further than this function.
  const invalid = details.invalid;
  if (Array.isArray(invalid)) {
    for (const entry of invalid) {
      if (!isRecord(entry)) continue;
      const { path, reason } = entry;
      if (typeof path !== "string" || path === "") continue;
      out.push({ path, reason: finalStepReasonCopy(reason, labels) });
    }
  }
  // The singular form. Read only when the plural gave nothing, so a refusal
  // carrying both cannot double up on one field.
  if (out.length === 0 && typeof details.field === "string") {
    const path = details.field;
    const reason = typeof body.error === "string" ? body.error : "";
    const ownerId = details.ownerId;
    if (path !== "" && reason.trim() !== "") {
      out.push({
        path,
        reason,
        ...(typeof ownerId === "string" && ownerId !== "" ? { ownerId } : {}),
      });
    }
  }
  return out;
}

/**
 * WHERE a refusal points, and nothing it SAYS (BRK-S268).
 *
 * The path and the person of every well-formed `details.fields` entry, read by
 * the same `fieldErrorsOf` the anchoring uses. The reason text is deliberately
 * left behind: this is for a caller deciding which STEP a refusal is about — a
 * repeat request whose refused save was of a step the broker never saw — and a
 * caller that only needs to know where to send the broker has no business
 * holding the server's words. Empty for anything that is not a structured
 * refusal (NN #7: a malformed body degrades to "nowhere", never a throw).
 */
export function refusedFieldPaths(
  err: unknown,
): { path: string; ownerId?: string }[] {
  if (!(err instanceof ApiError)) return [];
  return fieldErrorsOf(err.body).map(({ path, ownerId }) =>
    ownerId === undefined ? { path } : { path, ownerId },
  );
}

/**
 * The candidate keys for a refusal the server ATTRIBUTED to one person.
 *
 * A roster step scopes its field names per person (`<ownerId>::path`,
 * BRK-S152), so a placeable per-person reason is the scoped form of whatever
 * `candidatesFor` would have produced. Built from the same candidate chain —
 * the index strip, the address collapse — rather than a second one, so an
 * attributed reason is placed by exactly the rules an unattributed one is.
 */
function scopedCandidates(path: string, ownerId: string): string[] {
  return candidatesFor(path, false).map((c) => scopedRosterName(ownerId, c));
}

/**
 * `personal[0].ssn` → `personal[].ssn`; unchanged when there is no index at all;
 * null for any index OTHER than 0 — an index we cannot render is unanchorable
 * rather than approximately anchorable.
 *
 * Returning the path unchanged for the index-less case matters (BRK-S102): it
 * previously returned null, which ended the candidate chain before the composite
 * collapse below could run, so an index-less path could never reach it.
 */
function withoutLeadIndex(path: string): string | null {
  const match = /\[(\d+)\]/.exec(path);
  if (match === null) return path;
  return match[1] === "0" ? path.replace(/\[0\]/, "[]") : null;
}

/**
 * The members an address composite is built from — a reason about any one of
 * them is a reason about the address the renderer actually drew.
 *
 * `line1` / `line2` / `postalCode` / `country` STAY, and that is not the legacy
 * tolerance normalization Phase 3 removed elsewhere (BRK-S110). These are not
 * value shapes this portal accepts; they are the spellings an ERROR PATH arrives
 * under, and an error path is written in the grammar of the WRITE surface the
 * vendor rejected — which is exactly where `line1` legitimately lives. Narrowing
 * this set to the canonical read spelling would not reject a bad value; it would
 * only fail to place a real reason under the control the broker can fix, sending
 * it to the step summary instead.
 *
 * Over-collapsing stays harmless by construction: these are candidate keys, and
 * a key the step never rendered is never used.
 */
const ADDRESS_MEMBERS = new Set([
  "street",
  "line1",
  "line2",
  "addressLine2",
  "city",
  "state",
  "zip",
  "postalCode",
  "country",
]);

/**
 * `business.addresses[].city` → `business.addresses[]`.
 *
 * An `address` field is ONE control drawn from four inputs, so the server naming
 * a member has no control of its own to sit under; the composite is the thing
 * the broker can see and fix. Over-collapsing is harmless by construction:
 * candidates are only proposals, and a key the step never rendered is never used.
 */
function addressCompositeOf(path: string): string | null {
  const cut = path.lastIndexOf(".");
  if (cut <= 0) return null;
  return ADDRESS_MEMBERS.has(path.slice(cut + 1)) ? path.slice(0, cut) : null;
}

/**
 * The bag keys a server path could be naming, in order of directness.
 *
 * `rendered` is the authority, not this list — a candidate the step never drew
 * is simply skipped, which is what lets the collapse above be generous.
 *
 * SNAKE_CASE IS DELIBERATELY NOT BRIDGED. A path like `business_phone_numbers`
 * is the VENDOR's spelling, and discovery already maps vendor keys back to
 * contract paths and drops the ones it cannot resolve (SUR-S047). So a
 * snake_case path arriving here means an upstream mapping failed — bridging it
 * with a guessed transform would paper over that defect and invent a mapping
 * this repo does not own. It lands in the step summary instead: reported to the
 * broker, and still visible as an upstream bug rather than silently absorbed.
 */
function candidatesFor(path: string, echoesOurKeys: boolean): string[] {
  const out: string[] = [path];
  if (!echoesOurKeys) {
    const stripped = withoutLeadIndex(path);
    // A non-zero index: no control exists for it, so stop rather than approximate.
    if (stripped === null) return [];
    if (stripped !== path) out.push(stripped);
  }
  for (const base of [...out]) {
    const composite = addressCompositeOf(base);
    if (composite !== null) out.push(composite);
  }
  return out;
}

/**
 * Split a failed save into inline field errors plus one step-level line.
 *
 * `fieldNames` are the bag keys the step actually rendered — anything naming
 * something else belongs in the summary, where it is reported rather than
 * silently dropped.
 */
export function anchorServerError(
  err: unknown,
  fieldNames: readonly string[],
  /** Configured labels by key — used to name a field in the step-level line
   *  when it has no control on this step, and by cross-field reasons
   *  (FINAL_STEP_INVALID, BRK-S296). Optional: every other caller anchors
   *  without them. */
  labels: FieldLabels = {},
): AnchoredServerError {
  if (!(err instanceof ApiError)) {
    return { byField: {}, summary: UNKNOWN };
  }

  // A 4xx with no code is not a structured refusal, so nothing it says is
  // shown — not its `error`, not a reason it attached (BRK-S279).
  const uncoded = apiErrorCode(err) === null && uncodedRefusalMessage(err);
  if (uncoded) return { byField: {}, summary: uncoded };

  const rendered = new Set(fieldNames);
  const fields = fieldErrorsOf(err.body, labels);
  const code = apiErrorCode(err);
  // Its reasons are portal copy already (see `fieldErrorsOf`).
  const finalStep = code === FINAL_STEP_INVALID;

  // The insured has nobody holding a share. Answered before the generic path
  // because the server's own message describes the STATE, and the broker needs
  // the ACTION — and because this rejection names no fields to place.
  if (code === NO_OWNER_ON_ACCOUNT) {
    const anchor = ownershipShareField(rendered);
    return {
      byField: anchor === null ? {} : { [anchor]: NO_OWNER_SUMMARY },
      summary: NO_OWNER_SUMMARY,
    };
  }

  // BRK-S371 — the EIN already belongs to another account in the brokerage.
  // In the portal's words, on the EIN control (`einConflict.ts`); the page adds
  // the link to the holder. Without a control it stands at step level.
  if (code === EIN_ALREADY_EXISTS) {
    const anchor = einConflictPlacement(err, fieldNames);
    return anchor === null
      ? { byField: {}, summary: EIN_CONFLICT_COPY }
      : { byField: { [anchor]: EIN_CONFLICT_COPY }, summary: FIX_HIGHLIGHTED };
  }

  // BRK-S511 — the bond a rider or claim names is not an issued one. PMS
  // publishes it in TWO detail shapes: the rider's `details.fields[]`
  // (PMS-S850) and the claim's `details.field` (PMS-S851). Both name the
  // bond, so both land on the bond control with PMS's own sentence, and the
  // shape never decides whether the broker is told which input to fix.
  if (code === NO_ISSUED_BOND) {
    const named =
      isRecord(err.body) &&
      isRecord(err.body.details) &&
      typeof err.body.details.field === "string"
        ? err.body.details.field
        : (fields[0]?.path ?? "bondId");
    const anchor = rendered.has(named) ? named : null;
    return anchor === null
      ? { byField: {}, summary: err.message }
      : { byField: { [anchor]: err.message }, summary: FIX_HIGHLIGHTED };
  }

  // INVALID_FIELD_VALUE echoes the keys we sent, so its paths must be matched
  // as-is; stripping one would be matching a grammar it never uses.
  const echoesOurKeys = code === INVALID_FIELD_VALUE;

  // VALIDATION_FAILED speaks in the portal's words only (BRK-S279): each
  // reason — PMS's closed set, or discovery's free text read as `invalid` —
  // becomes portal copy, and the server's `error` is never the summary.
  const validation = code === VALIDATION_FAILED;
  // BRK-S317 — portal copy on both fields. It cannot say WHICH other person
  // from the refusal alone, so it names neither; the portal's own pre-save
  // check (`duplicateSsnErrors`) is the one that names the row.
  const ssnDuplicate = code === SSN_DUPLICATE;

  const byField: Record<string, string> = {};
  const unplaced: string[] = [];
  for (const { path, reason, ownerId } of fields) {
    const clean = ssnDuplicate
      ? SSN_DUPLICATE_COPY
      : validation
        ? validationReasonCopy(readValidationReason(reason))
        : stripRoutable(reason);
    if (clean === "") continue;
    // WHOSE field, when the refusal says (PMS-S393). The same contract path is
    // drawn once per signer on a roster step, so the path alone points at
    // several controls at once — `details.ownerId` is what makes a per-person
    // reason placeable rather than a step-level line. Tried FIRST and only
    // when given: without it a positional guess would put somebody else's
    // rejection under this person's name, which is the attribution the owner
    // roster exists to retire.
    const scoped = ownerId === undefined ? [] : scopedCandidates(path, ownerId);
    const name =
      scoped.find((c) => rendered.has(c)) ??
      candidatesFor(path, echoesOurKeys).find((c) => rendered.has(c)) ??
      null;
    if (name !== null) {
      // First reason wins: a field with two reasons shows the first rather than
      // concatenating them into an unreadable line.
      byField[name] ??= clean;
    } else if (finalStep) {
      // No control on this step: name the field by its configured label, or —
      // when even that is unknown — say nothing about it but the portal's
      // generic line. The raw key is never the name (BRK-S296).
      const label = labels[path];
      unplaced.push(
        label === undefined ? VALIDATION_SUMMARY : `${label}: ${clean}`,
      );
    } else {
      unplaced.push(clean);
    }
  }

  if (finalStep && unplaced.length === 0) {
    // The server's `error` lists raw keys ("Required: …", "Not valid: …"), so
    // it is never the fallback here.
    return {
      byField,
      summary: fields.length > 0 ? FIX_HIGHLIGHTED : VALIDATION_SUMMARY,
    };
  }

  if (validation) {
    // Every named field carried by a control → the usual "highlighted" line;
    // anything left over has no control, so the portal's own sentence.
    const named = validationFailures(err)?.length ?? 0;
    const allPlaced =
      named > 0 && unplaced.length === 0 && named === fields.length;
    return {
      byField,
      summary: allPlaced ? FIX_HIGHLIGHTED : VALIDATION_SUMMARY,
    };
  }

  return { byField, summary: summaryFor(fields, unplaced, err.message) };
}

const FIX_HIGHLIGHTED = "Please fix the highlighted fields and try again.";

const SSN_DUPLICATE_COPY = duplicateSsnMessage(
  "another person on this request",
);

/**
 * The step-level line.
 *
 * Built from the reasons that could NOT be placed rather than from the server's
 * summary, which is built from every reason and would therefore repeat inline
 * text. This way the line says precisely what the broker cannot see under an
 * input — and the server's summary is still the fallback for the case with no
 * per-field detail at all (`fields` arrives empty on a real rejection: upstream
 * drops a vendor key it cannot resolve, and a reason that sanitises to nothing).
 */
function summaryFor(
  fields: readonly ServerFieldError[],
  unplaced: readonly string[],
  message: string,
): string {
  // Distinct: one sentence is routinely repeated across every field it applies to.
  if (unplaced.length > 0) return [...new Set(unplaced)].join(" ");
  if (fields.length > 0) return FIX_HIGHLIGHTED;
  return stripRoutable(message) || GENERIC;
}
